// src/components/ml/FlowSenseAIExplainer.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — "Ask FlowSense AI": a chat that explains the system, its modules and every
// AI/ML model, grounded on live FlowSense data (POST /ml-ops/explain/ask).
//
//   <FlowSenseAIExplainer focus="DISPATCHER" />       full chat (Intelligence Center tab)
//   <ExplainButton topic="RUL_PREDICTION" />          small button → drawer with an instant
//                                                     explanation + the chat focused on it
//
// The answer badge says who answered: the local LLM (Ollama) or the built-in explainer
// (used when the LLM is offline/busy — it never pretends).
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge, Box, Button, Drawer, DrawerBody, DrawerCloseButton, DrawerContent, DrawerHeader, DrawerOverlay,
  Flex, HStack, Icon, IconButton, Input, InputGroup, InputRightElement, Select, Skeleton, Spinner, Text, Tooltip,
  VStack, Wrap, WrapItem, useClipboard, useColorModeValue, useDisclosure, useToast,
} from '@chakra-ui/react';
import { MdAutoAwesome, MdContentCopy, MdDeleteSweep, MdHelpOutline, MdSend } from 'react-icons/md';
import mlOpsService from '../../services/mlOpsService';

const WELCOME_SUGGESTIONS = [
  'What does each FlowSense AI model do?',
  'What needs attention right now?',
  'How does autopilot decide to retrain a model?',
  'How do I train and promote a new model version?',
];

// ── Minimal, safe markdown (bold, italic, bullets, numbered lists, paragraphs) ──
function inline(text, keyBase) {
  const parts = [];
  const re = /(\*\*[^*]+\*\*|_[^_]+_)/g;
  let last = 0; let m; let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    parts.push(tok.startsWith('**')
      ? <Text as="strong" key={`${keyBase}-${i++}`} fontWeight="700">{tok.slice(2, -2)}</Text>
      : <Text as="em" key={`${keyBase}-${i++}`}>{tok.slice(1, -1)}</Text>);
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

export function MiniMarkdown({ text }) {
  const blocks = useMemo(() => {
    const out = [];
    let list = null;
    String(text || '').split('\n').forEach((raw) => {
      const line = raw.trimEnd();
      const bullet = /^\s*(?:[•\-*])\s+(.*)$/.exec(line);
      const num = /^\s*(\d+)[.)]\s+(.*)$/.exec(line);
      if (bullet || num) {
        const kind = bullet ? 'ul' : 'ol';
        if (!list || list.kind !== kind) { list = { kind, items: [] }; out.push(list); }
        list.items.push(bullet ? bullet[1] : num[2]);
        return;
      }
      list = null;
      if (!line.trim()) { out.push({ kind: 'gap' }); return; }
      out.push({ kind: 'p', text: line });
    });
    return out;
  }, [text]);
  return (
    <Box fontSize="sm" lineHeight="1.6">
      {blocks.map((b, i) => {
        if (b.kind === 'gap') return <Box key={i} h={2} />;
        if (b.kind === 'p') return <Text key={i}>{inline(b.text, i)}</Text>;
        return (
          <Box as={b.kind} key={i} pl={5} my={1} sx={{ li: { mb: 0.5 } }}>
            {b.items.map((it, j) => <li key={j}>{inline(it, `${i}-${j}`)}</li>)}
          </Box>
        );
      })}
    </Box>
  );
}

function SourceBadge({ msg }) {
  if (msg.role !== 'assistant' || !msg.source) return null;
  const llm = msg.source === 'ollama';
  return (
    <Tooltip label={llm ? `Answered by the local LLM (${msg.model || 'Ollama'}) from live FlowSense data` : (msg.fallbackReason || 'Answered by the built-in explainer from the FlowSense catalog and live data')} hasArrow>
      <Badge colorScheme={llm ? 'purple' : 'teal'} variant="subtle" fontSize="0.65em">
        {llm ? `LLM · ${msg.model || 'ollama'}` : 'Built-in explainer'}{msg.durationMs ? ` · ${(msg.durationMs / 1000).toFixed(1)}s` : ''}
      </Badge>
    </Tooltip>
  );
}

function CopyButton({ text }) {
  const { onCopy, hasCopied } = useClipboard(text || '');
  return (
    <Tooltip label={hasCopied ? 'Copied' : 'Copy answer'} hasArrow>
      <IconButton aria-label="Copy answer" icon={<Icon as={MdContentCopy} />} size="xs" variant="ghost" onClick={onCopy} />
    </Tooltip>
  );
}

export default function FlowSenseAIExplainer({ focus: focusProp = '', initialQuestion = null, height = '560px', showFocusPicker = true, intro = null }) {
  const toast = useToast();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [focus, setFocus] = useState(focusProp || '');
  const [topics, setTopics] = useState([]);
  const [status, setStatus] = useState(null);
  const mounted = useRef(true);
  const scrollRef = useRef(null);
  const askedInitial = useRef(false);

  const panelBg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const userBg = useColorModeValue('purple.500', 'purple.400');
  const botBg = useColorModeValue('gray.50', 'gray.700');
  const muted = useColorModeValue('gray.500', 'gray.400');
  const chipBg = useColorModeValue('purple.50', 'whiteAlpha.100');

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { setFocus(focusProp || ''); }, [focusProp]);

  useEffect(() => {
    let alive = true;
    mlOpsService.assistantStatus().then((s) => { if (alive) setStatus(s); }).catch(() => { if (alive) setStatus({ online: false }); });
    if (showFocusPicker) {
      mlOpsService.getCatalog().then((c) => {
        if (!alive) return;
        setTopics([
          ...c.trainable.map(m => ({ key: m.key, label: m.name, group: 'Trainable models' })),
          ...c.engines.map(e => ({ key: e.key, label: e.name, group: 'AI engines' })),
        ]);
      }).catch(() => {});
    }
    return () => { alive = false; };
  }, [showFocusPicker]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, loading]);

  const send = useCallback(async (text) => {
    const question = String(text ?? input).trim();
    if (!question || loading) return;
    setInput('');
    const history = messages.slice(-6).map(m => ({ role: m.role, content: m.content }));
    setMessages(prev => [...prev, { role: 'user', content: question, at: Date.now() }]);
    setLoading(true);
    try {
      const r = await mlOpsService.ask({ question, focus: focus || undefined, history });
      if (!mounted.current) return;
      setMessages(prev => [...prev, { role: 'assistant', content: r.answer, source: r.source, model: r.model, fallbackReason: r.fallbackReason, durationMs: r.durationMs, suggestions: r.suggestions || [], topics: r.topics || [], at: Date.now() }]);
    } catch (e) {
      if (!mounted.current) return;
      setMessages(prev => [...prev, { role: 'assistant', content: `Sorry — I could not answer that: ${e.message}`, error: true, at: Date.now() }]);
      if (e.status === 429) toast({ title: 'Slow down a little', description: e.message, status: 'warning', duration: 4000 });
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [focus, input, loading, messages, toast]);

  useEffect(() => {
    if (initialQuestion && !askedInitial.current) { askedInitial.current = true; send(initialQuestion); }
  }, [initialQuestion, send]);

  const lastSuggestions = useMemo(() => {
    const last = [...messages].reverse().find(m => m.role === 'assistant' && m.suggestions?.length);
    return last ? last.suggestions : WELCOME_SUGGESTIONS;
  }, [messages]);

  return (
    <Flex direction="column" h={height} bg={panelBg} borderWidth="1px" borderColor={border} borderRadius="xl" overflow="hidden">
      <Flex px={4} py={3} borderBottomWidth="1px" borderColor={border} align="center" gap={3} wrap="wrap">
        <HStack spacing={2} flex="1" minW="200px">
          <Icon as={MdAutoAwesome} color="purple.400" boxSize={5} />
          <Box>
            <Text fontWeight="700" fontSize="sm">Ask FlowSense AI</Text>
            <Text fontSize="xs" color={muted}>
              {status === null ? 'Checking the assistant…' : status.online ? `Local LLM online (${status.model})` : 'LLM offline — built-in explainer answers from live data'}
            </Text>
          </Box>
        </HStack>
        {showFocusPicker && (
          <Select size="sm" maxW="240px" value={focus} onChange={e => setFocus(e.target.value)} aria-label="Focus the answers on">
            <option value="">All of FlowSense</option>
            {['Trainable models', 'AI engines'].map(g => (
              <optgroup key={g} label={g}>
                {topics.filter(t => t.group === g).map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </optgroup>
            ))}
          </Select>
        )}
        <Tooltip label="Clear conversation" hasArrow>
          <IconButton aria-label="Clear conversation" icon={<Icon as={MdDeleteSweep} />} size="sm" variant="ghost" onClick={() => setMessages([])} isDisabled={!messages.length || loading} />
        </Tooltip>
      </Flex>

      <Box ref={scrollRef} flex="1" overflowY="auto" px={4} py={3}>
        {messages.length === 0 && (
          <VStack align="stretch" spacing={3} py={2}>
            <Text fontSize="sm" color={muted}>
              {intro || 'Ask anything about FlowSense — what a module does, how a model makes its predictions, what data it learns from, why a version was promoted, or what needs attention.'}
            </Text>
          </VStack>
        )}
        <VStack align="stretch" spacing={3}>
          {messages.map((m, i) => (
            <Flex key={i} justify={m.role === 'user' ? 'flex-end' : 'flex-start'}>
              <Box maxW="88%" bg={m.role === 'user' ? userBg : botBg} color={m.role === 'user' ? 'white' : undefined}
                px={3} py={2} borderRadius="lg" borderWidth={m.error ? '1px' : 0} borderColor="red.300">
                {m.role === 'user' ? <Text fontSize="sm">{m.content}</Text> : <MiniMarkdown text={m.content} />}
                {m.role === 'assistant' && !m.error && (
                  <HStack mt={2} spacing={2}>
                    <SourceBadge msg={m} />
                    <CopyButton text={m.content} />
                  </HStack>
                )}
              </Box>
            </Flex>
          ))}
          {loading && (
            <Flex>
              <Box bg={botBg} px={3} py={2} borderRadius="lg" w="70%">
                <HStack mb={2}><Spinner size="xs" color="purple.400" /><Text fontSize="xs" color={muted}>Reading live FlowSense data…</Text></HStack>
                <Skeleton h="10px" mb={1} /><Skeleton h="10px" w="80%" />
              </Box>
            </Flex>
          )}
        </VStack>
      </Box>

      <Box px={4} pt={2} pb={3} borderTopWidth="1px" borderColor={border}>
        <Wrap spacing={2} mb={2}>
          {lastSuggestions.map(s => (
            <WrapItem key={s}>
              <Button size="xs" variant="ghost" bg={chipBg} borderRadius="full" fontWeight="500" onClick={() => send(s)} isDisabled={loading} whiteSpace="normal" textAlign="left" h="auto" py={1}>
                {s}
              </Button>
            </WrapItem>
          ))}
        </Wrap>
        <InputGroup size="md">
          <Input placeholder="Ask about a model, a module or a decision…" value={input} maxLength={1000}
            onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} isDisabled={loading} pr="3rem" />
          <InputRightElement>
            <IconButton aria-label="Send" icon={<Icon as={MdSend} />} size="sm" colorScheme="purple" variant="ghost" onClick={() => send()} isLoading={loading} isDisabled={!input.trim()} />
          </InputRightElement>
        </InputGroup>
      </Box>
    </Flex>
  );
}

/** Small "Explain" button: drawer with an instant explanation of `topic`, then the chat focused on it. */
export function ExplainButton({ topic, label = 'Explain', size = 'xs', variant = 'ghost', ...rest }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [explanation, setExplanation] = useState(null);
  const [error, setError] = useState(null);
  const muted = useColorModeValue('gray.500', 'gray.400');

  useEffect(() => {
    if (!isOpen) return undefined;
    let alive = true;
    setExplanation(null); setError(null);
    mlOpsService.explain(topic).then((r) => { if (alive) setExplanation(r); }).catch((e) => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, [isOpen, topic]);

  return (
    <>
      <Tooltip label="Explain this with FlowSense AI" hasArrow>
        <Button size={size} variant={variant} leftIcon={<Icon as={MdHelpOutline} />} onClick={onOpen} colorScheme="purple" {...rest}>{label}</Button>
      </Tooltip>
      <Drawer isOpen={isOpen} onClose={onClose} size="md" placement="right">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            <HStack><Icon as={MdAutoAwesome} color="purple.400" /><Text>{explanation?.title || 'FlowSense AI'}</Text></HStack>
          </DrawerHeader>
          <DrawerBody py={4}>
            <VStack align="stretch" spacing={4}>
              <Box>
                {!explanation && !error && <><Skeleton h="12px" mb={2} /><Skeleton h="12px" mb={2} w="90%" /><Skeleton h="12px" w="70%" /></>}
                {error && <Text fontSize="sm" color="red.400">{error}</Text>}
                {explanation && <MiniMarkdown text={explanation.text} />}
              </Box>
              <Text fontSize="xs" color={muted}>Ask a follow-up question:</Text>
              <FlowSenseAIExplainer focus={topic} height="420px" showFocusPicker={false} intro="Follow-up questions are answered with this topic in focus." />
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}
